import React, { useCallback, useState } from "react";
import { noop } from "lodash";
import { Input, Row, Col, Form, Modal, Select, AutoComplete } from "antd";
import { useTranslation } from "react-i18next";
import { getUserNameFromEmail } from "src/utils/helpers";
import FormItem from "src/components/form-item";
import "./style.scss";
import { getResponsibleByLogin } from "src/apis/autofill";

/**
 * Renders a form modal component.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onClose - Callback function invoked when the modal is closed.
 * @param {Function} props.createResponsible - Callback function for creating a responsible.
 * @param {Function} props.updateResponsible - Callback function for updating a responsible.
 * @param {Boolean} props.loading - Indicates if the form is in a loading state.
 * @param {Object} props.responsible - The responsible object to be edited in the form.
 * @param {Array<Object>} props.profiles - An array of profiles objects for the responsible.
 * @param {Array<Object>} props.positionMaps - Position maps objects for the responsible.
 * @returns {JSX.Element} The rendered form modal component.
 * @example
 *  <FormModal
        onClose={() => setCreatemodal(false)}
        createResponsible={(data) => createResponsible(data)}
        updateResponsible={(data) => updateResponsible(data)}
        loading={false}
        responsible={createModal}
        profiles={profiles}
        positionMaps={positionMaps}
    />
 */
function FormModal({
  onClose,
  createResponsible,
  updateResponsible,
  loading,
  responsible,
  profiles,
  positionMaps,
}) {
  const [data, setData] = useState(
    responsible && responsible.id ? responsible : {}
  );
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const requiredRule = {
    required: true,
    message: t("requiredField"),
  };
  const ciandtEmailPatternRule = {
    pattern: '^[a-zA-z0-9.\-]+@ciandt.com$',
    message: t("enterValidCitEmail"),
  };
  const gutter = 10;

  const [autoCompleteResult, setAutoCompleteResult] = useState([]);

  const onEmailChange = (value) => {
    if (!value) {
      setAutoCompleteResult([]);
    } else {
      setAutoCompleteResult(
        ["@ciandt.com"].map((domain) => `${value}${domain}`)
      );
    }
  };

  const emailOptions = autoCompleteResult.map((email) => ({
    label: email,
    value: email,
  }));

  /**
   * Call onClose function when canceling action or closing modal.
   */
  const onCancel = useCallback(() => {
    if (loading) {
      return;
    }

    onClose();
  }, [onClose, loading]);

  /**
   * Call update function if responsible and responsibleId exist, call create function otherwise.
   */
  const onClickSave = () => {
    const login = data.email.split("@")[0];

    if (responsible && responsible.id) {
      updateResponsible({ ...data, login });
    } else {
      createResponsible({ ...data, login });
    }
  };

  /**
   * Triggered when form is submitted.
   */
  const onFormSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  /**
   * Add all form field values into data object.
   * @param {string} prop - key.
   * @param {any} value - value.
   */
  const setValue = (prop, value) => {
    setData({
      ...data,
      [prop]: value,
    });
  };

  const handleEmailConfirmation = async (email) => {
    try {
      const response = await getResponsibleByLogin(getUserNameFromEmail(email));
      const selected = response.data.result;
      if (selected) {
        const updatedData = {
          name: selected.name,
          email: selected.email,
          profile: selected.profile,
          positionMap: selected.positionMap,
          gu: selected.gu,
        };

        setData(updatedData);
        form.setFieldsValue(updatedData);
      }
    } catch (error) {
      console.error("Error fetching responsible by login:", error);
    }
  };

  return (
    <Modal
      confirmLoading={loading}
      title={t("addResponsible")}
      onCancel={onCancel}
      onOk={onFormSubmit}
      okButtonProps={{
        className: "mr-10 cit-btn uppercase primary-type",
      }}
      cancelButtonProps={{
        className: "cit-btn link-type uppercase cancel-button",
      }}
      width={1000}
      visible={true}
      okText={t("save")}
      cancelText={t("cancel")}
      aria-modal="true"
    >
      <Form
        form={form}
        id="deallocation-form"
        initialValues={data}
        onFinish={onClickSave}
      >
        <Row gutter={gutter}>
          <Col span={12}>
            <FormItem isResponsible={true} label="Email">
              <Form.Item rules={[requiredRule, ciandtEmailPatternRule]} name="email">
                <AutoComplete options={emailOptions} onChange={onEmailChange}>
                  <Input
                    onChange={(e) => setValue("email", e.target.value)}
                    value={data.email}
                    placeholder={t("enterCollaboratorEmail")}
                    aria-label={t("collaboratorEmail")}
                    aria-placeholder={t("enterCollaboratorEmail")}
                    onBlur={async () => {
                      if (data.email) {
                        await handleEmailConfirmation(data.email);
                      }
                    }}
                  />
                </AutoComplete>
              </Form.Item>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem isResponsible={true} label={t("collaborator")}>
              <Form.Item rules={[requiredRule]} name="name">
                <Input
                  onChange={(e) => setValue("name", e.target.value)}
                  name={data.name}
                  placeholder={t("enterCollaboratorName")}
                  aria-label={t("collaboratorName")}
                  aria-placeholder={t("enterCollaboratorName")}
                />
              </Form.Item>
            </FormItem>
          </Col>
        </Row>
        <Row gutter={gutter}>
          <Col span={12}>
            <FormItem isResponsible={true} label={t("profile")}>
              <Form.Item name="profile" rules={[requiredRule]}>
                <Select
                  onChange={(e) => setValue("profile", e)}
                  placeholder={t("selectCollaboratorProfile")}
                  mode={"single"}
                  options={profiles}
                  aria-label={t("profile")}
                  aria-placeholder={t("selectCollaboratorProfile")}
                />
              </Form.Item>
            </FormItem>
          </Col>
          <Col span={12}>
            <FormItem isResponsible={true} label="Position Map">
              <Form.Item name="positionMap" rules={[requiredRule]}>
                <Select
                  onChange={(e) => setValue("positionMap", e)}
                  placeholder={t("selectCollaboratorPositionMap")}
                  mode={"single"}
                  options={positionMaps}
                  aria-label={t("positionMap")}
                  aria-placeholder={t("selectCollaboratorPositionMap")}
                />
              </Form.Item>
            </FormItem>
          </Col>
        </Row>
        <Row gutter={gutter}>
          <Col span={12}>
            <FormItem isResponsible={true} label="GU">
              <Form.Item name="gu" rules={[requiredRule]}>
                <Input
                  onChange={(e) => setValue("gu", e.target.value)}
                  placeholder={t("enterCollaboratorGU")}
                  aria-label={t("guAlliance")}
                  aria-placeholder={t("enterCollaboratorGU")}
                />
              </Form.Item>
            </FormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

FormModal.defaultProps = {
  onClose: noop,
  createResponsible: noop,
  updateResponsible: noop,
  loading: false,
  responsible: {},
  profiles: [],
  positionMaps: {},
};

export default FormModal;
